import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Key,
  Settings,
  Plug,
  Users,
  FileText,
  ShoppingCart,
  Ticket,
} from 'lucide-react'
import api from '../../services/api'
import { ERP_META, type ErpHelpEntry, type Step } from './erpHelpMeta'

export type { Step, ErpHelpEntry } from './erpHelpMeta'

// ── Types ─────────────────────────────────────────────────────────────────────

interface HelpCategory {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  iconBg: string
  defaultOpen?: boolean
}

// ── Guide category IDs that have actual content (not "coming soon") ───────────

const GUIDE_IDS_WITH_CONTENT = ['users', 'invoices', 'catalog', 'tickets'] as const
type GuideId = (typeof GUIDE_IDS_WITH_CONTENT)[number]

interface GuideEntry {
  id: GuideId
  steps: Step[]
  tip?: string
  stepColor: string
  stepTextColor: string
}

// ── Category static metadata (icons, colors — language-agnostic) ──────────────

const CATEGORY_META = [
  {
    id: 'erp',
    icon: <Plug className="w-4 h-4" />,
    iconBg: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    defaultOpen: true,
  },
  {
    id: 'users',
    icon: <Users className="w-4 h-4" />,
    iconBg: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
  },
  {
    id: 'invoices',
    icon: <FileText className="w-4 h-4" />,
    iconBg: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
  },
  {
    id: 'catalog',
    icon: <ShoppingCart className="w-4 h-4" />,
    iconBg: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
  },
  {
    id: 'tickets',
    icon: <Ticket className="w-4 h-4" />,
    iconBg: 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400',
  },
]

// ── Sub-components ────────────────────────────────────────────────────────────

export function StepBadge({ number, color }: { number: number; color: string }) {
  return (
    <div
      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 ${color}`}
    >
      {number}
    </div>
  )
}

export function TipBox({ text }: { text: string }) {
  return (
    <div className="flex gap-2 p-3 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <Lightbulb className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-green-800 dark:text-green-300 leading-relaxed">{text}</p>
    </div>
  )
}

export function CodeSnippet({ code }: { code: string }) {
  return (
    <code className="inline-block mt-1 px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600">
      {code}
    </code>
  )
}

function GenericGuideList({ entry }: { entry: GuideEntry }) {
  return (
    <div className="px-4 py-5">
      <ol className="space-y-4">
        {entry.steps.map((step, idx) => (
          <li key={idx} className="flex gap-3">
            <StepBadge number={idx + 1} color={`${entry.stepColor} ${entry.stepTextColor}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-0.5">
                {step.title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {step.content}
              </p>
              {step.code && <CodeSnippet code={step.code} />}
            </div>
          </li>
        ))}
      </ol>
      {entry.tip && <TipBox text={entry.tip} />}
    </div>
  )
}

export function ErpAccordionItem({
  entry,
  defaultOpen,
  officialDocLabel,
}: {
  entry: ErpHelpEntry
  defaultOpen?: boolean
  officialDocLabel: string
}) {
  const [open, setOpen] = useState(defaultOpen ?? false)

  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
      >
        <div
          className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${entry.color} ${entry.textColor}`}
        >
          {entry.label.substring(0, 2)}
        </div>
        <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">
          {entry.label}
        </span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-5 pl-14">
          <ol className="space-y-4">
            {entry.steps.map((step, idx) => (
              <li key={idx} className="flex gap-3">
                <StepBadge number={idx + 1} color={`${entry.color} ${entry.textColor}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-0.5">
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {step.content}
                  </p>
                  {step.code && <CodeSnippet code={step.code} />}
                </div>
              </li>
            ))}
          </ol>

          {entry.tip && <TipBox text={entry.tip} />}

          {entry.docUrl && (
            <a
              href={entry.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Key className="w-3 h-3" />
              {officialDocLabel}
            </a>
          )}
        </div>
      )}
    </div>
  )
}

function CategorySection({
  category,
  children,
  isEmpty,
  comingSoonLabel,
}: {
  category: HelpCategory
  children?: React.ReactNode
  isEmpty?: boolean
  comingSoonLabel: string
}) {
  const [open, setOpen] = useState(category.defaultOpen ?? false)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={isEmpty}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent text-left"
      >
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${category.iconBg}`}
        >
          {category.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{category.title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{category.description}</p>
        </div>
        {isEmpty ? (
          <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full flex-shrink-0">
            {comingSoonLabel}
          </span>
        ) : open ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {open && !isEmpty && (
        <div className="border-t border-gray-100 dark:border-gray-700">{children}</div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminHelpTooltips() {
  const { t } = useTranslation()
  const user = api.getUser()
  const isSuperAdmin = user?.role === 'super_admin'

  // Build ERP data by merging static metadata with translated content
  const erpHelpData: ErpHelpEntry[] = ERP_META.map((meta) => ({
    ...meta,
    tip: t(`adminHelpTooltips.erps.${meta.erp_type}.tip`),
    steps: t(`adminHelpTooltips.erps.${meta.erp_type}.steps`, { returnObjects: true }) as Step[],
  }))

  // Build category data by merging static metadata with translated labels
  const helpCategories: HelpCategory[] = CATEGORY_META.map((meta) => ({
    ...meta,
    title: t(`adminHelpTooltips.categories.${meta.id}.title`),
    description: t(`adminHelpTooltips.categories.${meta.id}.description`),
  }))

  const comingSoonLabel = t('adminHelpTooltips.badgeComingSoon')
  const officialDocLabel = t('adminHelpTooltips.officialDoc')

  // Build guide data for categories that have content
  const GUIDE_STYLE: Record<GuideId, { stepColor: string; stepTextColor: string }> = {
    users: {
      stepColor: 'bg-purple-100 dark:bg-purple-900/40',
      stepTextColor: 'text-purple-700 dark:text-purple-300',
    },
    invoices: {
      stepColor: 'bg-amber-100 dark:bg-amber-900/40',
      stepTextColor: 'text-amber-700 dark:text-amber-300',
    },
    catalog: {
      stepColor: 'bg-green-100 dark:bg-green-900/40',
      stepTextColor: 'text-green-700 dark:text-green-300',
    },
    tickets: {
      stepColor: 'bg-rose-100 dark:bg-rose-900/40',
      stepTextColor: 'text-rose-700 dark:text-rose-300',
    },
  }

  const guideEntries: Record<GuideId, GuideEntry> = Object.fromEntries(
    GUIDE_IDS_WITH_CONTENT.map((id) => [
      id,
      {
        id,
        tip: t(`adminHelpTooltips.guides.${id}.tip`),
        steps: ((): Step[] => {
          const raw = t(`adminHelpTooltips.guides.${id}.steps`, { returnObjects: true })
          return Array.isArray(raw) ? (raw as Step[]) : []
        })(),
        ...GUIDE_STYLE[id],
      },
    ])
  ) as Record<GuideId, GuideEntry>

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
          <HelpCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('adminHelpTooltips.pageTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {isSuperAdmin
              ? t('adminHelpTooltips.subtitleSuperAdmin')
              : t('adminHelpTooltips.subtitleAdmin')}
          </p>
        </div>
      </div>

      {/* ERP connection section — super_admin only */}
      {isSuperAdmin && (
        <div>
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">
            {t('adminHelpTooltips.sectionErpLabel')}
          </p>
          <CategorySection
            category={helpCategories[0]}
            isEmpty={erpHelpData.length === 0}
            comingSoonLabel={comingSoonLabel}
          >
            {erpHelpData.map((entry) => (
              <ErpAccordionItem
                key={entry.erp_type}
                entry={entry}
                defaultOpen={false}
                officialDocLabel={officialDocLabel}
              />
            ))}
          </CategorySection>
        </div>
      )}

      {/* Usage guides section — visible to all */}
      <div>
        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">
          {t('adminHelpTooltips.sectionUsageLabel')}
        </p>
        <div className="space-y-3">
          {helpCategories.slice(1).map((category) => {
            const guideId = category.id as GuideId
            const hasContent = (GUIDE_IDS_WITH_CONTENT as readonly string[]).includes(guideId)
            return (
              <CategorySection
                key={category.id}
                category={category}
                isEmpty={!hasContent}
                comingSoonLabel={comingSoonLabel}
              >
                {hasContent ? <GenericGuideList entry={guideEntries[guideId]} /> : null}
              </CategorySection>
            )
          })}
        </div>
      </div>

      {/* Footer note */}
      <div className="flex gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <Settings className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('adminHelpTooltips.footerNote')}
        </p>
      </div>
    </div>
  )
}
